import { Location } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnDestroy, Output } from '@angular/core';
import {
  IStatsList,
  ITeacherCardComponentState,
  StampEnum,
  TeacherCardTabType,
} from '@app/teachers/teacher-card/teacher-card-variables';
import { IHorizontalScrollItem } from '@core/components/horizontal-scroll-menu/horizontal-scroll-menu.component';
import {
  AccountService,
  AuthService,
  BillingV2Service,
  ConfigService,
  ContextService,
  FilesService,
} from '@core/services';
import { ChangableComponent } from '@models/changable.component';
import { Store } from '@ngrx/store';
import { ApplicationDialogsService } from '@shared/application-dialogs';
import { loadFilteredLanguages, loadSubjectsV2 } from '@store/actions/data.actions';
import { differenceInMinutes } from 'date-fns';
import {
  Country,
  IAccountStats,
  ITeachingSubject,
  Language,
  Subject as BaseSubject,
  SubjectCategoryEnum,
  SubjectsRegistry,
  User,
  UserProfile,
  UserStatusEnum,
} from 'lingo2-models';
import { DeviceDetectorService } from 'ngx-device-detector';
import { OnUiCover } from 'onclass-ui';
import { combineLatest, Observable, of, timer } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-teacher-card',
  templateUrl: './teacher-card.component.html',
  styleUrls: ['./teacher-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TeacherCardComponent extends ChangableComponent implements OnDestroy {
  deviceService = inject(DeviceDetectorService);
  authService = inject(AuthService);
  billingV2Service = inject(BillingV2Service);
  configService = inject(ConfigService);
  contextService = inject(ContextService);
  store = inject(Store);
  location = inject(Location);
  dialogsService = inject(ApplicationDialogsService);

  @Output() public favoriteChanged = new EventEmitter<boolean>();
  public stamp: StampEnum = StampEnum.top;
  public subjectCategoryEnum = SubjectCategoryEnum;
  public profileUrl: string[];
  public fullName: string;
  public firstName: string;
  public lastName: string;
  public avatar: OnUiCover;
  public lastActiveAt: Date | string;
  public statusOnline: boolean;
  public country: Country;
  public profile: Partial<UserProfile>;
  public teachingSubject: ITeachingSubject;
  public stats: Partial<IAccountStats>;
  public speakLanguages: Language[] = [];
  public statsList: IStatsList[] = [];
  public isMe: boolean;
  public isGuest: boolean;
  public isExpandPanel: boolean;
  public checkoutModalOpen = false;
  public activeTab: TeacherCardTabType = 'teaching';
  public state: ITeacherCardComponentState = {
    isMediaPrepared: false,
    open: false,
    tabsExpand: false,
    openMobileModal: false,
    closeModal: false,
    opened_popup: false,
    activeServiceIndex: null,
    complainForm: false,
    subscribed: false,
  };
  public openTeachingSubjectsTooltip: boolean;
  public openComplainForm: boolean;
  public teachingSubjectsList: IHorizontalScrollItem[] = [];
  public surfaceBlockDisplay: boolean;
  public canSignup$: Observable<boolean> = this.isBrowser
    ? timer(0, 60 * 1000).pipe(map(() => this.latest_applicable_date?.getTime() > Date.now()))
    : of(true).pipe(map(() => this.latest_applicable_date?.getTime() > Date.now()));

  private _teacher: User;
  private _me: User;
  private _languages: Language[];
  private _subjects: SubjectsRegistry;
  private originalUrl: string;

  public debug$ = this.contextService.debug$;

  @Input()
  public set teacher(teacher: User) {
    this._teacher = teacher;
    this.prepareTeacher();
  }

  public get teacher(): User {
    return this._teacher;
  }

  public get me() {
    return this._me;
  }

  private prepareTeacher() {
    this.profileUrl = AccountService.accountRoute(this._teacher);
    this.fullName = AccountService.getUserFullName(this._teacher);
    this.firstName = this._teacher?.first_name;
    this.lastName = this._teacher?.last_name;

    this.avatar = {
      type: 'avatar',
      img:
        this.prepareStorageUrl(this._teacher?.__opts?.images[0].md) ||
        FilesService.getFileUrlBySize(this._teacher?.userpic_id),
      link: this.profileUrl.join('/'),
      title: this.fullName,
      form: 'circle',
    };
    this.lastActiveAt = this._teacher?.last_active_at;
    this.statusOnline = differenceInMinutes(new Date(), new Date(this._teacher?.last_active_at)) <= 5;
    this.country = this._teacher?.country;
    this.profile = this._teacher?.profile;
    const [first] = this.profile.teaching_subjects;
    this.teachingSubject = first;
    this.stats = this._teacher?.stats;
    this.statsList = [
      {
        name: 'lessons_count',
        icon: 'stat-class',
        color: '#A4B0C3',
        title: 'teacher-card.stats.classes',
        value: this.stats?.meetings_count || 0,
      },
      {
        name: 'students_count',
        icon: 'stat-students',
        color: '#A4B0C3',
        title: 'teacher-card.stats.students',
        value: this.stats?.students_count || 0,
      },
    ];

    combineLatest([this.configService.languages$, this.configService.subjectsV2$])
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: ([languages, subjects]) => {
          this._languages = languages;
          this._subjects = subjects;
          this.speakLanguages = this._teacher?.profile?.spoken_languages
            .sort((a, b) => +a.level_id - +b.level_id)
            .map((_sl) => this._languages.find((_lang) => +_lang.id === +_sl.language_id));
          this.teachingSubjectsList = this.generateTeachingSubjectsList();
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          this.store.dispatch(loadFilteredLanguages({ locale: this.me.ui_language }));
          this.store.dispatch(loadSubjectsV2({ locale: this.me.ui_language }));
        },
      });

    this.contextService.me$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (me) => {
        this._me = me;
        this.isGuest = me && me.status === UserStatusEnum.guest;
        this.isMe = me?.id === this._teacher?.id;
        this.detectChanges();
      },
      error: (error) => {
        this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
      },
    });
  }

  public getFormattedAmount(): Observable<string> {
    return this.billingV2Service.formatAmount$(this.stats.min_price_tier);
  }

  public findSubjectById(teaching: ITeachingSubject): BaseSubject {
    return [
      ...this._subjects.native,
      ...this._subjects.foreign,
      ...this._subjects.non_categorized,
      ...this._subjects.user,
      ...this._subjects.recent,
      ...this._subjects.other,
    ].find((_s) => +_s.id === +teaching?.subject_id);
  }

  public onToggleExpandPanel() {
    this.isExpandPanel = !this.isExpandPanel;
    this.setAndResetUrl();
    this.detectChanges();
  }

  public onOpenAbout() {
    this.onToggleExpandPanel();
  }

  public onIinViewportChange(e: boolean) {
    this.surfaceBlockDisplay = e;
    this.detectChanges();
  }

  private setAndResetUrl() {
    const url = this.profileUrl.join('/');
    const currentUrl = this.location.path(false);
    if (this.isExpandPanel) {
      if (currentUrl !== url) {
        if (!this.originalUrl) {
          this.originalUrl = currentUrl;
        }
        this.location.replaceState(url);
      }
    } else {
      if (this.originalUrl) {
        this.location.replaceState(this.originalUrl);
        this.originalUrl = null;
      }
    }
  }

  public onSelectedTeachingSubject(teach: number | string) {
    this.teachingSubject = this.profile?.teaching_subjects[+teach];
    this.onToggleExpandPanel();
    this.detectChanges();
  }

  public onOpenComplainForm() {
    this.openComplainForm = true;
  }

  public onCloseComplainForm() {
    this.openComplainForm = false;
  }

  public openCheckoutModal() {
    const onAuthComplete = () => {
      this.checkoutModalOpen = true;
      this.dialogsService.openUserServiceCheckoutWizard({
        teacherId: this.teacher.id,
      });
      this.detectChanges();
    };

    if (this.isGuest) {
      this.authService.showAuthModal(onAuthComplete);
    } else {
      onAuthComplete();
    }
  }

  public closeCheckoutModal() {
    this.checkoutModalOpen = false;
    this.detectChanges();
  }

  public onOpenTeachingSubjects() {
    this.openTeachingSubjectsTooltip = true;
  }

  public onCloseTeachingSubjects() {
    this.openTeachingSubjectsTooltip = false;
  }

  public subjectsTransform(teachingSubjects: ITeachingSubject[]) {
    return teachingSubjects.map((_ts) => ({
      title: this.findSubjectById(_ts)?.title,
      native: this.findSubjectById(_ts)?.category_id === SubjectCategoryEnum.native_language,
    }));
  }

  public onFavoriteChanged() {
    this.favoriteChanged.emit(true);
  }

  protected get latest_applicable_date(): Date {
    return this._teacher?.stats?.latest_applicable_date ? new Date(this._teacher?.stats?.latest_applicable_date) : null;
  }

  private generateTeachingSubjectsList(): IHorizontalScrollItem[] {
    return this.profile.teaching_subjects.map((_ts, index) => ({
      value: index,
      title: this.findSubjectById(_ts)?.title || '???',
      native: this.findSubjectById(_ts)?.category_id === SubjectCategoryEnum.native_language,
    }));
  }

  public trackByFn(index) {
    return index;
  }
}
